<template>
  <div
    ref="homeContainer"
    :class="layoutMode === 'classic' ? 'classic-layout' : 'properties-page'"
  >
    <!-- ==================== SKELETON LOADING STATE ==================== -->
    <template v-if="!searchedApartments">
      <div class="skeleton-container">
        <!-- Skeleton Header -->
        <div class="skeleton-header-section">
          <div class="skeleton-header-content">
            <div class="skeleton-header-text">
              <div class="skeleton-title"></div>
              <div class="skeleton-subtitle"></div>
            </div>
            <div class="skeleton-stats-card">
              <div class="skeleton-stats-icon"></div>
              <div class="skeleton-stats-info">
                <div class="skeleton-stats-number"></div>
                <div class="skeleton-stats-label"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Skeleton Properties Grid -->
        <div class="skeleton-properties-grid">
          <div
            v-for="n in 3"
            :key="'skeleton-card-' + n"
            class="skeleton-property-card"
          >
            <!-- Skeleton Card Image -->
            <div class="skeleton-card-image">
              <div class="skeleton-badge"></div>
            </div>

            <!-- Skeleton Card Content -->
            <div class="skeleton-card-content">
              <div class="skeleton-property-title"></div>
              <div class="skeleton-property-location"></div>

              <!-- Skeleton Features -->
              <div class="skeleton-features">
                <div class="skeleton-feature-item"></div>
                <div class="skeleton-feature-item"></div>
                <div class="skeleton-feature-item"></div>
              </div>

              <!-- Skeleton Actions -->
              <div class="skeleton-card-actions">
                <div class="skeleton-btn-primary"></div>
                <div class="skeleton-btn-secondary"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ==================== CLASSIC LAYOUT ==================== -->
    <template v-else-if="layoutMode === 'classic'">
      <!-- Page Header with Stats (only show in grid view) -->
      <div class="classic-header-section" v-if="viewMode === 'grid'">
        <div class="classic-header-content">
          <div class="classic-header-text">
            <h2 class="classic-title">Mis Propiedades</h2>
            <p class="classic-subtitle">
              Selecciona una tarjeta para gestionar tu propiedad, ver documentos
              y realizar seguimiento.
            </p>
          </div>
          <div class="classic-stats-card" v-if="apartments.length > 0">
            <div class="stats-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 9.5L12 4L21 9.5"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M19 13V19.4C19 19.7314 18.7314 20 18.4 20H5.6C5.26863 20 5 19.7314 5 19.4V13"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div class="stats-info">
              <span class="stats-number">{{ apartments.length }}</span>
              <span class="stats-label"
                >{{
                  apartments.length === 1 ? "Propiedad" : "Propiedades"
                }}
                registradas</span
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Properties Grid -->
      <div v-if="apartments.length > 0 && viewMode === 'grid'">
        <div class="classic-properties-grid">
          <div
            v-for="apt in apartments"
            :key="apt.asset_id"
            class="classic-property-card"
            @click="selectAndViewApartment(apt)"
          >
            <!-- Card Image -->
            <div class="classic-card-image" :style="getAssetImageStyle(apt)">
              <div class="classic-image-overlay"></div>
              <div class="classic-badge" :class="getBadgeClass(apt)">
                {{ getBadgeText(apt) }}
              </div>
            </div>

            <!-- Card Content -->
            <div class="classic-card-content">
              <h4 class="classic-property-title">
                {{ getPropertyTitle(apt) }}
              </h4>
              <p class="classic-property-location">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 21C12 21 19 15.5 19 10C19 6.13401 15.866 3 12 3C8.13401 3 5 6.13401 5 10C5 15.5 12 21 12 21Z"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <circle
                    cx="12"
                    cy="10"
                    r="2"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                </svg>
                {{ getProjectName(apt) }}
              </p>

              <!-- Property Features -->
              <div
                class="classic-features"
                v-if="
                  apt.useful_surface ||
                  apt.bedrooms ||
                  apt.bathrooms ||
                  apt.cellars
                "
              >
                <div class="classic-feature-item" v-if="apt.useful_surface">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M3 3V21H21"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M7 15L12 10L17 15"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <span>{{ apt.useful_surface }} m²*</span>
                </div>
                <div class="classic-feature-item" v-if="apt.bedrooms">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M3 7V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V7"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                    <path
                      d="M21 13H3"
                      stroke="currentColor"
                      stroke-width="1.5"
                    />
                    <path
                      d="M3 10C3 8.89543 3.89543 8 5 8H8C9.10457 8 10 8.89543 10 10V13H3V10Z"
                      stroke="currentColor"
                      stroke-width="1.5"
                    />
                    <path
                      d="M14 10C14 8.89543 14.8954 8 16 8H19C20.1046 8 21 8.89543 21 10V13H14V10Z"
                      stroke="currentColor"
                      stroke-width="1.5"
                    />
                  </svg>
                  <span>{{ apt.bedrooms }} Dorm.</span>
                </div>
                <div class="classic-feature-item" v-if="apt.bathrooms">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M4 12H20V16C20 18.2091 18.2091 20 16 20H8C5.79086 20 4 18.2091 4 16V12Z"
                      stroke="currentColor"
                      stroke-width="1.5"
                    />
                    <path
                      d="M6 12V5C6 3.89543 6.89543 3 8 3H9C10.1046 3 11 3.89543 11 5V6"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                  </svg>
                  <span
                    >{{ apt.bathrooms }} Baño{{
                      apt.bathrooms > 1 ? "s" : ""
                    }}</span
                  >
                </div>
                <div class="classic-feature-item" v-if="apt.cellars">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M3 21H21"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                    <path
                      d="M5 21V7L12 3L19 7V21"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M9 21V15H15V21"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <span
                    >{{ apt.cellars }} Bodega{{
                      apt.cellars > 1 ? "s" : ""
                    }}</span
                  >
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="classic-card-actions">
                <button
                  class="classic-btn-primary"
                  @click.stop="selectAndViewApartment(apt)"
                >
                  Ver Detalles
                </button>
                <button
                  class="classic-btn-secondary"
                  @click.stop="goToDocuments(apt)"
                >
                  Documentos
                </button>
              </div>
            </div>
          </div>
        </div>
        <p
          class="full-width small"
          style="font-size: 0.7em; font-style: italic; margin-top: 20px"
        >
          *Tamaños aproximados
        </p>
      </div>
      <!-- Empty State -->
      <div
        v-else-if="searchedApartments && apartments.length === 0"
        class="classic-empty-state"
      >
        <b-img
          :src="require('@/assets/images/no-apartments.png')"
          alt="no-apartments-img"
          class="classic-empty-img"
        />
        <p>No tienes propiedades registradas</p>
      </div>

      <!-- Classic Detail View -->
      <div
        v-if="viewMode === 'detail' && selectedApartment"
        class="classic-detail-view"
      >
        <!-- Back Button -->
        <div v-if="apartments.length > 1" class="classic-detail-back">
          <button class="classic-back-btn" @click="goBackToGrid">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 18L9 12L15 6"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span>Volver a Mis Propiedades</span>
          </button>
        </div>

        <!-- Property Detail -->
        <div class="classic-detail-content">
          <apartments
            :asset="selectedApartment"
            :tab_index="tab_index"
            :features="features"
            :photos="photos"
            :images="images"
          />
        </div>
      </div>
    </template>

    <!-- ==================== MODERN LAYOUT ==================== -->
    <template v-else>
      <!-- Grid View -->
      <div v-if="viewMode === 'grid'" class="properties-grid-view">
        <!-- Page Header with Stats -->
        <div class="classic-header-section">
          <div class="classic-header-content">
            <div class="classic-header-text">
              <h2 class="classic-title">Mis Propiedades</h2>
              <p class="classic-subtitle">
                Selecciona una tarjeta para gestionar tu propiedad, ver
                documentos y realizar seguimiento.
              </p>
            </div>
            <div class="classic-stats-card" v-if="apartments.length > 0">
              <div class="stats-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 9.5L12 4L21 9.5"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M19 13V19.4C19 19.7314 18.7314 20 18.4 20H5.6C5.26863 20 5 19.7314 5 19.4V13"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <div class="stats-info">
                <span class="stats-number">{{ apartments.length }}</span>
                <span class="stats-label"
                  >{{
                    apartments.length === 1 ? "Propiedad" : "Propiedades"
                  }}
                  registradas</span
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Properties Grid (same as classic) -->
        <div class="classic-properties-grid" v-if="apartments.length > 0">
          <div
            v-for="apt in apartments"
            :key="apt.asset_id"
            class="classic-property-card"
            @click="selectAndViewApartment(apt)"
          >
            <!-- Card Image -->
            <div class="classic-card-image" :style="getAssetImageStyle(apt)">
              <div class="classic-image-overlay"></div>
              <div class="classic-badge" :class="getBadgeClass(apt)">
                {{ getBadgeText(apt) }}
              </div>
            </div>

            <!-- Card Content -->
            <div class="classic-card-content">
              <h4 class="classic-property-title">
                {{ getPropertyTitle(apt) }}
              </h4>
              <p class="classic-property-location">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 21C12 21 19 15.5 19 10C19 6.13401 15.866 3 12 3C8.13401 3 5 6.13401 5 10C5 15.5 12 21 12 21Z"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <circle
                    cx="12"
                    cy="10"
                    r="2"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                </svg>
                {{ getProjectName(apt) }}
              </p>

              <!-- Property Features -->
              <div
                class="classic-features"
                v-if="
                  apt.useful_surface ||
                  apt.bedrooms ||
                  apt.bathrooms ||
                  apt.cellars
                "
              >
                <div class="classic-feature-item" v-if="apt.useful_surface">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M3 3V21H21"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M7 15L12 10L17 15"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <span>{{ apt.useful_surface }} m²</span>
                </div>
                <div class="classic-feature-item" v-if="apt.bedrooms">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M3 7V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V7"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                    <path
                      d="M21 13H3"
                      stroke="currentColor"
                      stroke-width="1.5"
                    />
                    <path
                      d="M3 10C3 8.89543 3.89543 8 5 8H8C9.10457 8 10 8.89543 10 10V13H3V10Z"
                      stroke="currentColor"
                      stroke-width="1.5"
                    />
                    <path
                      d="M14 10C14 8.89543 14.8954 8 16 8H19C20.1046 8 21 8.89543 21 10V13H14V10Z"
                      stroke="currentColor"
                      stroke-width="1.5"
                    />
                  </svg>
                  <span>{{ apt.bedrooms }} Dorm.</span>
                </div>
                <div class="classic-feature-item" v-if="apt.bathrooms">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M4 12H20V16C20 18.2091 18.2091 20 16 20H8C5.79086 20 4 18.2091 4 16V12Z"
                      stroke="currentColor"
                      stroke-width="1.5"
                    />
                    <path
                      d="M6 12V5C6 3.89543 6.89543 3 8 3H9C10.1046 3 11 3.89543 11 5V6"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                  </svg>
                  <span>{{ apt.bathrooms }} Baños</span>
                </div>
                <div class="classic-feature-item" v-if="apt.cellars">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M3 21H21"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                    <path
                      d="M5 21V7L12 3L19 7V21"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M9 21V15H15V21"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <span
                    >{{ apt.cellars }} Bodega{{
                      apt.cellars > 1 ? "s" : ""
                    }}</span
                  >
                </div>
              </div>

              <!-- View Details Footer -->
              <div class="classic-card-footer">
                <span class="view-details-text">Ver detalles</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div
          v-if="searchedApartments && apartments.length === 0"
          class="empty-state"
        >
          <div class="empty-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 9.5L12 4L21 9.5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M19 13V19.4C19 19.7314 18.7314 20 18.4 20H5.6C5.26863 20 5 19.7314 5 19.4V13"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <h3 class="empty-title">No tienes propiedades registradas</h3>
          <p class="empty-text">
            Cuando adquieras una propiedad, podrás verla y gestionarla desde
            aquí.
          </p>
        </div>

        <!-- CTA Banner -->
        <div class="cta-banner" v-if="apartments.length > 0">
          <div class="cta-content">
            <h3 class="cta-title">¿Interesado en más propiedades?</h3>
            <p class="cta-text">Explora nuestro catálogo completo</p>
          </div>
          <router-link
            v-if="country == 1 && showQuote"
            :to="{ name: 'quote' }"
            class="cta-button"
          >
            Cotizar Nuevas
          </router-link>
        </div>
      </div>

      <!-- Detail View -->
      <div v-else class="properties-detail-view">
        <!-- Back Button (outside white container) -->
        <div v-if="apartments.length > 1" class="detail-back">
          <button class="back-btn" @click="goBackToGrid">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 18L9 12L15 6"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span>Volver a Mis Propiedades</span>
          </button>
        </div>

        <!-- Main Content Area -->
        <div class="detail-layout-wrapper">
          <div class="detail-main-area">
            <div class="detail-container">
              <!-- Property Detail Card -->
              <div class="detail-content" v-if="selectedApartment">
                <apartments
                  :asset="selectedApartment"
                  :tab_index="tab_index"
                  :features="features"
                  :photos="photos"
                  :images="images"
                />
              </div>

              <!-- No Property Selected -->
              <div v-else-if="searchedApartments" class="empty-state">
                <b-img
                  :src="require('@/assets/images/no-apartments.png')"
                  alt="no-apartments-img"
                  class="empty-image"
                />
              </div>
            </div>
          </div>

          <!-- Sidebar (outside white container, like classic layout) -->
          <div v-if="sidebarData.length > 0" class="detail-sidebar">
            <sidebar :sidebar-data="sidebarData" />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
<script>
import apartments from "../views/apartments/Apartments";
import Sidebar from "../views/sidebar/Sidebar";

// TODO: Constante para modal de cambio de contraseña - deshabilitado temporalmente
// const DEFAULT_PASSWORD_WARNING_KEY = "ms_default_password_warning";
export default {
  name: "index",
  components: {
    apartments,
    Sidebar,
  },
  props: {
    customer: { default: null },
    layoutMode: { default: "modern" },
  },
  data() {
    return {
      viewMode: "detail",
      asset_id: null,
      apartments: [],
      tab_index: 0,
      selectedApartment: null,
      sidebarData: [],
      features: [],
      photos: [],
      images: [],
      searchedApartments: false,
    };
  },
  mounted() {
    this.setApartments();

    // Listen for go-back event from TopBar
    this.$root.$on("go-back-to-grid", this.goBackToGrid);

    if (this.$route.query.card_response) {
      if (this.$route.query.card_response == "true") {
        this.$swal("Éxito!", "Tarjeta Agregada con éxito", "success").then(
          () => {
            if (this.apartments.length > 1) {
              return this.$swal(
                "¡Debes agregar bienes a tu tarjeta!",
                "favor ve a la pestaña pagos automaticos, pincha el boton agregar bienes y señala cual quieres usar en tu tarjeta",
                "info"
              );
            }
            return null;
          }
        );
        // .then(() => {
        //   this.maybeShowDefaultPasswordWarning();
        // });
      } else {
        this.$swal(
          "Parece que hubo un error agregando tu tarjeta!",
          "",
          "info"
        );
        // .then(() => {
        //   this.maybeShowDefaultPasswordWarning();
        // });
      }
    }
    // else {
    //   this.maybeShowDefaultPasswordWarning();
    // }
  },
  beforeDestroy() {
    this.$root.$off("go-back-to-grid", this.goBackToGrid);
  },
  methods: {
    maybeShowDefaultPasswordWarning() {
      // TODO: Modal de cambio de contraseña deshabilitado temporalmente.
      // Para reactivar, descomentar el código de abajo y las llamadas a este método.
      return;

      /*
      // Solo mostrar al entrar al home principal post-login
      if (this.$route.name !== "index") return;

      let payload = null;
      try {
        payload = JSON.parse(
          localStorage.getItem(DEFAULT_PASSWORD_WARNING_KEY) || "null"
        );
      } catch (e) {
        payload = null;
      }

      const currentCustomer = this.$route?.params?.customer || this.customer;
      if (
        !payload ||
        !payload.customer ||
        payload.customer !== currentCustomer
      ) {
        return;
      }

      const clearFlag = () => {
        try {
          localStorage.removeItem(DEFAULT_PASSWORD_WARNING_KEY);
        } catch (e) {
          // ignore
        }
      };

      this.$swal({
        title: "Recomendación de seguridad",
        text: "Detectamos que ingresaste con la contraseña por defecto (primeros 6 dígitos de tu RUT). Te recomendamos cambiarla por seguridad.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Cambiar ahora",
        cancelButtonText: "Más tarde",
      })
        .then((result) => {
          if (result && result.isConfirmed) {
            return this.$router.push({
              name: "information",
              params: { customer: currentCustomer },
              query: { scrollTo: "password" },
            });
          }
          return null;
        })
        .finally(() => {
          clearFlag();
        });
      */
    },
    getTabIndexFromRoute() {
      // Soporta ambos formatos:
      // - ?tab_index=2
      // - ?tab=documents | documentos
      const rawTabIndex = this.$route?.query?.tab_index;
      if (
        rawTabIndex !== undefined &&
        rawTabIndex !== null &&
        rawTabIndex !== ""
      ) {
        const parsed = Number(rawTabIndex);
        return Number.isFinite(parsed) ? parsed : 0;
      }

      const tab = String(this.$route?.query?.tab || "").toLowerCase();
      if (tab === "documents" || tab === "documentos") {
        // En Options.vue el índice de Documentos depende de si existe el tab de Pagos.
        // Tabs:
        // 0: Detalle
        // 1: Pagos (si show_payment)
        // 1/2: Documentos (si show_document)
        return this.showPayment ? 2 : 1;
      }
      return 0;
    },
    goBackToGrid() {
      this.viewMode = "grid";
      this.selectedApartment = null;
      // Clear sidebar data so the right sidebar disappears
      this.sidebarData = [];
      // Emit events to clear UI state
      this.$root.$emit("property-selected", null);
      this.$root.$emit("sidebar-data-changed", []);
      // Navigate back to index route
      if (this.$route.name === "asset-detail") {
        this.$router.push({
          name: "index",
          params: { customer: this.$route.params.customer },
        });
      }
    },
    emitPropertyInfo(apt) {
      const propertyInfo = {
        title: this.getPropertyTitle(apt),
        project: this.getProjectName(apt),
      };
      this.$root.$emit("property-selected", propertyInfo);
    },
    scrollToTop() {
      this.$nextTick(() => {
        const element = this.$refs.homeContainer;
        if (element) {
          const navbarHeight = 70; // Altura aproximada del navbar
          const extraOffset = 100; // Offset adicional para dar más espacio
          const elementPosition =
            element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - navbarHeight - extraOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      });
    },
    navigateToAsset(apt, options = {}) {
      // Navigate to asset-detail route with assetId and optional tab selector
      const query = { ...this.$route.query };
      if (options.tab_index !== undefined && options.tab_index !== null) {
        query.tab_index = options.tab_index;
        delete query.tab;
      } else if (options.tab) {
        query.tab = options.tab;
        delete query.tab_index;
      } else {
        // Si no se especifica tab, limpiamos el query para no “arrastrar” tabs previos
        delete query.tab;
        delete query.tab_index;
      }

      this.$router.push({
        name: "asset-detail",
        params: {
          customer: this.$route.params.customer,
          assetId: apt.asset_id,
        },
        query,
      });
    },
    selectApartment(apt) {
      this.selectedApartment = apt;
      this.tab_index = 0;
      this.viewMode = "detail";
      this.emitPropertyInfo(apt);
      this.navigateToAsset(apt, { tab_index: 0 });
      this.scrollToTop();
    },
    selectApartmentDocuments(apt) {
      this.selectedApartment = apt;
      this.tab_index = 2; // Documents tab
      this.viewMode = "detail";
      this.emitPropertyInfo(apt);
      this.navigateToAsset(apt, { tab: "documents" });
      this.scrollToTop();
    },
    selectAndViewApartment(apt) {
      // Show apartment detail view
      this.selectedApartment = apt;
      this.tab_index = 0;
      this.viewMode = "detail";
      this.emitPropertyInfo(apt);
      this.navigateToAsset(apt, { tab_index: 0 });
      this.scrollToTop();
    },
    goToDocuments(apt) {
      // Show apartment documents tab
      this.selectedApartment = apt;
      this.tab_index = 2; // Documents tab
      this.viewMode = "detail";
      this.emitPropertyInfo(apt);
      this.navigateToAsset(apt, { tab: "documents" });
      this.scrollToTop();
    },
    loadAssetFromRoute() {
      const assetId = this.$route.params.assetId;
      if (assetId && this.apartments.length > 0) {
        const apt = this.apartments.find((a) => a.asset_id == assetId);
        if (apt) {
          this.selectedApartment = apt;
          this.tab_index = this.getTabIndexFromRoute();
          this.viewMode = "detail";
          this.emitPropertyInfo(apt);
        } else {
          // Asset not found, redirect to home
          this.$router.replace({
            name: "index",
            params: { customer: this.$route.params.customer },
          });
        }
      } else if (
        assetId &&
        this.apartments.length === 0 &&
        this.searchedApartments
      ) {
        // No apartments found but we have an assetId, redirect to home
        this.$router.replace({
          name: "index",
          params: { customer: this.$route.params.customer },
        });
      }
    },
    getProjectName(apt) {
      return apt.project_name;
    },
    getPropertyTitle(apt) {
      return `${apt.asset_type.split("_").join(" ")} ${apt.number}`;
    },
    getBadgeClass(apt) {
      // You can add logic based on apt status
      return ["RESERVADO"].includes(apt.asset_state)
        ? "badge-active"
        : "badge-active";
    },
    getBadgeText(apt) {
      // You can add logic based on apt status
      return apt.asset_state;
    },
    getAssetImageStyle(apt) {
      const project = this.$store.getters.getProjectByMoby(apt.project_id);
      let url =
        project && project.urlImagenUnoProyectoCompleta
          ? project.urlImagenUnoProyectoCompleta
          : null;
      if (!url)
        url =
          project && project.urlLogoProyecto
            ? `https://s3.amazonaws.com/gestion.mobysuite/${project.urlLogoProyecto}`
            : "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiB2aWV3Qm94PSIwIDAgMjQgMjQiIGZpbGw9Im5vbmUiPjxyZWN0IHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgZmlsbD0iI2YzZjRmNiIvPjxwYXRoIGQ9Ik0yMSAxOVY1QzIxIDMuODk1NDMgMTkuMTA0NiAzIDE3IDNIN0M0Ljg5NTQzIDMgMyAzLjg5NTQzIDMgNVYxOUMzIDIwLjEwNDYgNC44OTU0MyAyMSA3IDIxSDE3QzE5LjEwNDYgMjEgMjEgMjAuMTA0NiAyMSAxOVoiIHN0cm9rZT0iIzljYTNhZiIgc3Ryb2tlLXdpZHRoPSIxLjUiLz48Y2lyY2xlIGN4PSI4LjUiIGN5PSI4LjUiIHI9IjIiIGZpbGw9IiM5Y2EzYWYiLz48cGF0aCBkPSJNMyAxNUw3IDExTDExIDE1TDE1IDExTDIxIDE3IiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMS41IiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz48L3N2Zz4=";
      return { backgroundImage: `url("${url}")` };
    },
    setApartments() {
      let self = this;
      this.axios
        .get("assets/")
        .then((result) => {
          this.searchedApartments = true;
          if (result.data.assets.length > 0) {
            this.apartments = result.data.assets;
            this.$store.commit("setApartments", result.data);

            // Check if we have an assetId in the route
            if (this.$route.params.assetId) {
              this.loadAssetFromRoute();
            } else if (this.apartments.length > 1) {
              this.viewMode = "grid";
              this.selectedApartment = null;
            } else {
              this.viewMode = "detail";
              this.selectedApartment = this.apartments[0];
              // Emitir la información del bien para mostrar la franja
              this.emitPropertyInfo(this.apartments[0]);
            }
          } else {
            if (this.customer == "clab" && this.apartments.length == 0) {
              this.axios
                .get(`projects/1/get_amc_features`)
                .then((result) => {
                  self.sidebarData = result.data.sidebarResponse
                    ? result.data.sidebarResponse.categoriasSidebars
                    : [];
                })
                .catch((error) => {
                  console.log(error);
                  return null;
                });
            }
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
  },
  watch: {
    $route: {
      handler: function (to, from) {
        // Handle back navigation
        if (to.name === "index" && from && from.name === "asset-detail") {
          this.viewMode = "grid";
          this.selectedApartment = null;
          this.sidebarData = [];
          this.$root.$emit("property-selected", null);
          this.$root.$emit("sidebar-data-changed", []);
        }
        // Handle direct navigation to asset
        if (to.name === "asset-detail" && to.params.assetId) {
          this.loadAssetFromRoute();
        }
      },
    },
    selectedApartment: {
      handler: function () {
        if (this.selectedApartment) {
          let self = this;
          this.axios
            .get(
              `projects/${this.selectedApartment.project_id}/get_amc_features`
            )
            .then((result) => {
              self.sidebarData = result.data.sidebarResponse
                ? result.data.sidebarResponse.categoriasSidebars
                : [];
              self.features = result.data.amcCaracteristicas
                ? result.data.amcCaracteristicas.filter((data) => data.activo)
                : [];
              self.photos = result.data.amcImagenes.filter(
                (data) => data.activo && data.tipo == "CARRUSEL"
              );
              self.images = result.data.amcImagenes.filter(
                (data) =>
                  data.activo &&
                  ["FOTO", "PRESENTACION", "PORTADA"].includes(data.tipo)
              );
            })
            .catch((error) => {
              console.log(error);
              return null;
            });
        }
      },
      immediate: true,
    },
    viewMode: {
      handler: function (newVal) {
        // Clear property indicator and sidebar when going back to grid
        if (newVal === "grid") {
          this.$root.$emit("property-selected", null);
          this.$root.$emit("sidebar-data-changed", []);
        }
      },
    },
    sidebarData: {
      handler: function (newVal) {
        this.$root.$emit("sidebar-data-changed", newVal);
      },
      immediate: true,
    },
  },
  computed: {
    project() {
      return this.selectedApartment
        ? this.$store.getters.getProjectByMoby(
            this.selectedApartment.project_id
          )
        : null;
    },
    logo() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.logo_1_url
        : null;
    },
    customerLabel() {
      return this.customer === "clab" ? "Inversiones" : "Propiedades";
    },
    country() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.country
        : 1;
    },
    showQuote() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.show_quote
        : false;
    },
    showPayment() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.show_payment
        : false;
    },
  },
};
</script>

<style scoped>
/* ===== Properties Page Premium Styles ===== */
.properties-page {
  /* Variables dinámicas basadas en el tema de la inmobiliaria */
  --theme-primary: var(--color-btn, #1e40af);
  --theme-primary-hover: var(--color-btn-a, #1e3a8a);
  --theme-primary-text: var(--color-btn-text, #ffffff);
  --theme-accent: var(--color-a, #1e40af);
}

/* ===== Grid View ===== */
.properties-grid-view {
  max-width: 1400px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
  padding: 24px;
  animation: containerFadeIn 0.4s ease;
}

@keyframes containerFadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Properties Grid */
.properties-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 24px;
}

/* Property Card - Similar to PropiNET */
.property-card {
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
}

.property-card:hover {
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12);
  border-color: var(--theme-primary);
}

/* Card Image */
.card-image {
  height: 220px;
  background-size: cover;
  background-position: center;
  background-color: #f3f4f6;
  position: relative;
  background-repeat: no-repeat;
}

/* Ajuste para el ícono SVG por defecto */
.card-image[style*="data:image/svg"] {
  background-size: 100px 100px;
  background-position: center;
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 50%, rgba(0, 0, 0, 0.2) 100%);
}

.property-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.property-badge.badge-active {
  background: #10b981;
  color: #ffffff;
}

.property-badge.badge-sold {
  background: #1e40af;
  color: #ffffff;
}

/* Card Content */
.card-content {
  padding: 20px;
}

.property-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px 0;
  text-transform: capitalize;
}

.property-location {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.property-location svg {
  color: #9ca3af;
}

.property-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 16px 0;
}

/* Property Features */
.property-features {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 12px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  color: #4b5563;
}

.feature-item svg {
  color: var(--color-btn-a, #3b82f6);
}

/* Card Actions */
.card-actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.btn-primary {
  flex: 1;
  padding: 12px 16px;
  background: var(--theme-primary);
  color: var(--theme-primary-text);
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary:hover {
  background: var(--theme-primary-hover);
  transform: translateY(-1px);
}

.btn-secondary {
  flex: 1;
  padding: 12px 16px;
  background: #ffffff;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

/* CTA Banner */
.cta-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 32px;
  padding: 24px 32px;
  background: #f9fafb;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
}

.cta-content {
  display: flex;
  flex-direction: column;
}

.cta-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 4px 0;
}

.cta-text {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.cta-button {
  padding: 12px 24px;
  background: var(--theme-primary);
  color: var(--theme-primary-text);
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.cta-button:hover {
  background: var(--theme-primary-hover);
  text-decoration: none;
  color: var(--theme-primary-text);
}

/* ===== Detail View ===== */
.properties-detail-view {
  max-width: 1400px;
  margin: 0 auto;
}

.detail-back {
  margin-bottom: 16px;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  color: #4b5563;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  animation: backBtnFadeIn 0.3s ease;
}

@keyframes backBtnFadeIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.back-btn:hover {
  background: #f9fafb;
  border-color: #d1d5db;
  color: #1f2937;
}

/* Wrapper for detail layout (content + sidebar) */
.detail-layout-wrapper {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.detail-main-area {
  flex: 1;
  min-width: 0;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
  padding: 24px;
  animation: containerFadeIn 0.4s ease;
}

.detail-container {
  width: 100%;
}

.detail-sidebar {
  width: 320px;
  flex-shrink: 0;
  position: sticky;
  top: 94px; /* TopBar height (70px) + page padding (24px) */
  align-self: flex-start;
  max-height: calc(100vh - 118px);
  overflow-y: auto;
}

/* ===== Empty State ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
}

.empty-icon {
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border-radius: 24px;
  color: #9ca3af;
  margin-bottom: 24px;
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
}

.empty-text {
  font-size: 0.9375rem;
  color: #6b7280;
  max-width: 400px;
}

.empty-image {
  max-width: 400px;
  width: 100%;
  opacity: 0.8;
}

/* ===== Responsive ===== */
@media (max-width: 1024px) {
  .detail-layout-wrapper {
    flex-direction: column;
  }

  .detail-sidebar {
    width: 100%;
    position: static;
  }
}

@media (max-width: 768px) {
  .properties-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .card-actions {
    flex-direction: column;
  }

  .cta-banner {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }
}

/* ===== CLASSIC LAYOUT STYLES ===== */
.classic-layout {
  background: transparent;
  min-height: 100vh;
}

/* Properties Grid */
.classic-properties-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

/* Header Section with Stats */
.classic-header-section {
  padding: 0;
  margin-bottom: 24px;
  animation: headerSectionFadeIn 0.5s ease;
}

@keyframes headerSectionFadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.classic-header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  flex-wrap: wrap;
}

.classic-header-text {
  flex: 1;
  min-width: 280px;
}

.classic-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px;
  animation: titleSlideIn 0.4s ease 0.1s backwards;
}

@keyframes titleSlideIn {
  from {
    opacity: 0;
    transform: translateX(-15px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.classic-subtitle {
  font-size: 0.9375rem;
  color: #6b7280;
  margin: 0;
  max-width: 500px;
  line-height: 1.5;
  animation: subtitleFadeIn 0.4s ease 0.2s backwards;
}

@keyframes subtitleFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Stats Card */
.classic-stats-card {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 24px;
  animation: statsCardSlide 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s
    backwards;
}

@keyframes statsCardSlide {
  from {
    opacity: 0;
    transform: translateX(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

.stats-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  background: var(--color-btn, linear-gradient(90deg, #3b82f6, #2563eb));
  border-radius: 12px;
  color: white;
  animation: iconPulse 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.5s backwards;
}

@keyframes iconPulse {
  from {
    opacity: 0;
    transform: scale(0);
  }
  60% {
    transform: scale(1.15);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.stats-info {
  display: flex;
  flex-direction: column;
}

.stats-number {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
  animation: numberCount 0.6s ease 0.6s backwards;
}

@keyframes numberCount {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stats-label {
  font-size: 0.8125rem;
  color: #6b7280;
  margin-top: 4px;
}

.classic-property-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  cursor: pointer;
  height: 100%;
  display: flex;
  flex-direction: column;
  animation: cardFadeIn 0.4s ease backwards;
}

.classic-property-card:nth-child(1) {
  animation-delay: 0.2s;
}
.classic-property-card:nth-child(2) {
  animation-delay: 0.3s;
}
.classic-property-card:nth-child(3) {
  animation-delay: 0.4s;
}
.classic-property-card:nth-child(4) {
  animation-delay: 0.5s;
}
.classic-property-card:nth-child(5) {
  animation-delay: 0.6s;
}
.classic-property-card:nth-child(6) {
  animation-delay: 0.7s;
}

@keyframes cardFadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.classic-property-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
}

.classic-card-image {
  position: relative;
  height: 160px;
  background-size: cover;
  background-position: center;
  background-color: #e5e7eb;
  background-repeat: no-repeat;
}

/* Ajuste para el ícono SVG por defecto */
.classic-card-image[style*="data:image/svg"] {
  background-size: 80px 80px;
  background-position: center;
}

.classic-image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    transparent 50%,
    rgba(0, 0, 0, 0.4) 100%
  );
}

.classic-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.classic-badge.badge-active {
  background: rgba(16, 185, 129, 0.9);
  color: white;
}

.classic-badge.badge-pending {
  background: rgba(245, 158, 11, 0.9);
  color: white;
}

.classic-badge.badge-default {
  background: rgba(107, 114, 128, 0.9);
  color: white;
}

.classic-card-content {
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.classic-property-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 6px;
  line-height: 1.3;
}

.classic-property-location {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0 0 16px;
}

.classic-property-location svg {
  flex-shrink: 0;
}

/* Property Features */
.classic-features {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 16px;
}

.classic-feature-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  color: #4b5563;
}

.classic-feature-item svg {
  color: var(--color-btn-a, #3b82f6);
  flex-shrink: 0;
}

.classic-card-actions {
  display: flex;
  gap: 8px;
  margin-top: auto;
}

.classic-btn-primary {
  flex: 1;
  padding: 10px 12px;
  background: var(--color-btn, linear-gradient(90deg, #3b82f6, #2563eb));
  color: var(--color-btn-text, white);
  border: none;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.classic-btn-primary:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.classic-btn-secondary {
  flex: 1;
  padding: 10px 12px;
  background: transparent;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.classic-btn-secondary:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.classic-empty-state {
  text-align: center;
  padding: 60px 20px;
}

.classic-empty-img {
  max-width: 300px;
  width: 100%;
  margin-bottom: 20px;
  opacity: 0.8;
}

.classic-empty-state p {
  color: #6b7280;
  font-size: 1rem;
}

/* Classic Detail View */

.classic-detail-back {
  margin-bottom: 20px;
}

.classic-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  color: #4b5563;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  padding: 10px 16px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.classic-back-btn:hover {
  background: #e5e7eb;
  color: #1f2937;
}

.classic-back-btn svg {
  flex-shrink: 0;
}

.classic-detail-content {
  background: #fff;
  border-radius: 12px;
}

/* Classic Layout Responsive */
@media (max-width: 1200px) {
  .classic-sidebar-section {
    width: 280px;
  }
}

@media (max-width: 991px) {
  .classic-two-column-layout {
    flex-direction: column;
  }

  .classic-sidebar-section {
    width: 100%;
  }

  .classic-sidebar-card {
    position: static;
  }
}

@media (max-width: 768px) {
  .classic-header-content {
    flex-direction: column;
    gap: 16px;
  }

  .classic-stats-card {
    width: 100%;
  }

  .classic-section-card {
    padding: 16px;
  }

  .classic-properties-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .classic-properties-grid {
    grid-template-columns: 1fr;
  }

  .classic-card-actions {
    flex-direction: column;
  }

  .classic-title {
    font-size: 1.5rem;
  }

  .classic-stats-card {
    padding: 14px 18px;
  }

  .stats-icon {
    width: 44px;
    height: 44px;
  }

  .stats-icon svg {
    width: 22px;
    height: 22px;
  }

  .stats-number {
    font-size: 1.5rem;
  }

  .classic-property-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 16px;
  }

  .classic-property-header-left {
    width: 100%;
  }

  .classic-property-icon {
    width: 40px;
    height: 40px;
  }

  .classic-property-icon svg {
    width: 20px;
    height: 20px;
  }

  .classic-property-name {
    font-size: 1rem;
  }

  .classic-property-project {
    font-size: 0.8rem;
  }

  .classic-property-badge-container {
    width: 100%;
    justify-content: flex-end;
  }
}

/* ===== SKELETON LOADING STYLES ===== */
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.skeleton-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Skeleton Header */
.skeleton-header-section {
  margin-bottom: 24px;
}

.skeleton-header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  flex-wrap: wrap;
}

.skeleton-header-text {
  flex: 1;
  min-width: 280px;
}

.skeleton-title {
  width: 200px;
  height: 32px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
  margin-bottom: 12px;
}

.skeleton-subtitle {
  width: 350px;
  height: 18px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
}

.skeleton-stats-card {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 24px;
}

.skeleton-stats-icon {
  width: 52px;
  height: 52px;
  background: linear-gradient(90deg, #d1d5db 25%, #e5e7eb 50%, #d1d5db 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 12px;
}

.skeleton-stats-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.skeleton-stats-number {
  width: 40px;
  height: 28px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
}

.skeleton-stats-label {
  width: 120px;
  height: 14px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

/* Skeleton Properties Grid */
.skeleton-properties-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.skeleton-property-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.skeleton-card-image {
  position: relative;
  height: 160px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  width: 70px;
  height: 22px;
  background: linear-gradient(90deg, #d1d5db 25%, #e5e7eb 50%, #d1d5db 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
}

.skeleton-card-content {
  padding: 16px;
}

.skeleton-property-title {
  width: 140px;
  height: 20px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
  margin-bottom: 8px;
}

.skeleton-property-location {
  width: 180px;
  height: 16px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 16px;
}

.skeleton-features {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 16px;
}

.skeleton-feature-item {
  width: 70px;
  height: 16px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-card-actions {
  display: flex;
  gap: 8px;
}

.skeleton-btn-primary,
.skeleton-btn-secondary {
  flex: 1;
  height: 40px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

/* Skeleton Responsive */
@media (max-width: 768px) {
  .skeleton-header-content {
    flex-direction: column;
    gap: 16px;
  }

  .skeleton-stats-card {
    width: 100%;
  }

  .skeleton-properties-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .skeleton-subtitle {
    width: 100%;
    max-width: 300px;
  }
}

@media (max-width: 576px) {
  .skeleton-properties-grid {
    grid-template-columns: 1fr;
  }

  .skeleton-card-actions {
    flex-direction: column;
  }

  .skeleton-title {
    width: 160px;
    height: 28px;
  }

  .skeleton-stats-card {
    padding: 14px 18px;
  }

  .skeleton-stats-icon {
    width: 44px;
    height: 44px;
  }
}
</style>
